import React, { useMemo } from 'react';
import PanelErrorBoundary from '../../shared/PanelErrorBoundary';
import { Skeleton } from '../../shared/Skeleton';
import { useTileData } from '../../../hooks/useTileData';
import { getSignals, getClientAttentionRow } from '../../../api/hub';
import { tintClasses, fmtNumber } from '../kit';
import { SURFACE } from '../layout';
import { signalTint } from '../signalTint';
import { BlockError, isPending } from './states';
import CountBadge from './CountBadge';

// The attention strip: one chip per signal, then a neutral last-contact chip. It
// answers "what should I know before I talk to them" at a glance, wrapping (never
// scrolling) when there are many.
//
// Signal text is the server-composed `says`, rendered VERBATIM — never rebuilt from
// other fields. Only the last-contact chip is composed here, from the numeric
// `days_quiet` on v_client_attention, by the same rule AttentionFinding uses:
// null = no record, 0 = today.
//
// `link` on a signal is a template with an {id} placeholder and no id to fill it
// (see SignalsPanel) — it is deliberately not rendered.

const SEVERITY_ORDER = { critical: 0, warn: 1, watch: 2 };

/**
 * The last-contact chip's text: "unknown" for no record, "today" for 0.
 * @param {number|null|undefined} daysQuiet
 * @returns {string}
 */
export const lastContactText = (daysQuiet) => {
  if (daysQuiet == null) return 'Last contact unknown';
  const n = Number(daysQuiet);
  if (!Number.isFinite(n)) return 'Last contact unknown';
  if (n === 0) return 'Last contact today';
  return `Last contact ${fmtNumber(n)} ${n === 1 ? 'day' : 'days'} ago`;
};

/** Loudest signal first; equal severities keep the server's order. */
const bySeverity = (list) =>
  list
    .map((s, i) => [s, i])
    .sort(([a, ai], [b, bi]) => (SEVERITY_ORDER[a?.severity] ?? 9) - (SEVERITY_ORDER[b?.severity] ?? 9) || ai - bi)
    .map(([s]) => s);

/**
 * One pill: tint dot + text, on ONE line. Real signal sentences can be a paragraph (several
 * "waiting on a reply about … — 20964 hours." joined together), so the pill is capped at
 * 36rem and the text is cut with an ellipsis; the whole sentence stays in the DOM and is
 * the pill's tooltip. That keeps the strip a row of compact pills, as designed, instead of
 * one pill the width of the page.
 */
const Chip = ({ tint, children, title, signal, testId = 'attention-chip' }) => {
  const t = tintClasses(tint);
  return (
    <li
      data-testid={testId}
      data-tint={tint}
      data-signal={signal || undefined}
      title={title}
      className={`inline-flex min-w-0 max-w-full items-center gap-2.5 rounded-lg border px-4 py-3 text-sm sm:max-w-xl ${t.surface} ${t.text}`}
    >
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${t.dot}`} aria-hidden="true" />
      <span className="min-w-0 truncate">{children}</span>
    </li>
  );
};

/**
 * @param {object} props
 * @param {string} props.clientId
 */
export default function AttentionStrip({ clientId }) {
  const signals = useTileData(() => getSignals(clientId), [clientId], !!clientId);
  const attention = useTileData(() => getClientAttentionRow(clientId), [clientId], !!clientId);

  const chips = useMemo(() => bySeverity(signals.data?.data || []), [signals.data]);
  const attentionRow = attention.data?.data ?? null;
  const loading = isPending(signals) || isPending(attention);

  return (
    <PanelErrorBoundary label="Attention">
      <section aria-label="Attention" className={`relative ${SURFACE} !px-5 !py-4`}>
        {/* The number of signals, on the card's top-right corner (the last-contact chip is not a signal). */}
        {!loading && chips.length > 0 && (
          <CountBadge count={chips.length} noun="signal" testId="attention-count" className="absolute -top-3 right-5" />
        )}
        {loading ? (
          <div className="flex flex-wrap gap-3" role="status" aria-label="Loading">
            <Skeleton className="h-11 w-56 rounded-lg" />
            <Skeleton className="h-11 w-64 rounded-lg" />
            <Skeleton className="h-11 w-48 rounded-lg" />
            <span className="sr-only">Loading…</span>
          </div>
        ) : (
          <>
            <ul className="flex flex-wrap gap-3">
              {chips.map((sig, i) => (
                <Chip key={sig?.signal ? `${sig.signal}-${i}` : i} tint={signalTint(sig)} title={sig?.says || sig?.label} signal={sig?.signal}>
                  {sig?.says || sig?.label}
                </Chip>
              ))}
              {attentionRow && (
                <Chip tint="neutral" testId="last-contact-chip">{lastContactText(attentionRow.days_quiet)}</Chip>
              )}
            </ul>
            {!signals.error && chips.length === 0 && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No active signals.</p>
            )}
            {signals.error && (
              <BlockError error={signals.error} what="this client's signals" padding="pt-2" message="Couldn't load this client's signals." onRetry={signals.refresh} />
            )}
            {attention.error && (
              <BlockError error={attention.error} what="this client's last-contact date" padding="pt-2" message="Couldn't load when we last spoke to this client." onRetry={attention.refresh} />
            )}
          </>
        )}
      </section>
    </PanelErrorBoundary>
  );
}
